import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { DiagnosticTieIn, RelatedPatterns } from "@/components/CaseExtras";
import ChecklistGate from "@/components/ChecklistGate";
import { useTitle } from "@/hooks/use-title";
import { YOUTUBE_URL, VIDEO_MANAGER_BOTTLENECK } from "@/data/content";

const ASK_THE_MANAGER = [
  "A decision needs to be made",
  "Something isn't clear",
  "An exception appears",
  "Someone needs to know how something works",
];

const OBVIOUS_RECOMMENDATIONS = [
  "Delegate more",
  "Prioritise better",
  "Hire another person",
  "Improve time management",
];

const UNDERNEATH_QUESTIONS = [
  "What decisions are actually reaching this person?",
  "Why do those decisions require them specifically?",
  "What knowledge exists only in their head?",
  "What would happen if they were unavailable for two weeks?",
];

const EQUATION_PARTS = ["Knowledge", "Decision-making", "Exceptions", "Approvals"];

const SIX_QUESTIONS = [
  "How many different processes currently require this person?",
  "What decisions can only they make?",
  "What information do other people have to ask them for?",
  "Which of those decisions are genuinely unique to their role?",
  "Which could be standardised, delegated or embedded into a process?",
  "What would stop working if this person disappeared tomorrow?",
];

const CHANGE_STEPS = [
  {
    n: "1",
    title: "Map the dependencies",
    text: "Identify where the organisation relies on the manager.",
  },
  {
    n: "2",
    title: "Separate judgement from routine decisions",
    text: "Not every decision needs managerial involvement.",
  },
  {
    n: "3",
    title: "Define decision rights",
    text: "Who can make each decision? Who must approve it? Who simply needs to be informed?",
    sub: ["Who can make each decision", "Who must approve it", "Who simply needs to be informed"],
  },
  {
    n: "4",
    title: "Capture critical knowledge",
    text: "Move repeatable organisational knowledge out of individual memory.",
  },
  {
    n: "5",
    title: "Design escalation around exceptions",
    text: "The manager should ideally deal with the things that genuinely require their judgement — not every routine question.",
  },
];

const TECH_LIST = [
  "Knowledge management",
  "Workflow routing",
  "Approvals",
  "Decision support",
  "Documentation",
  "Notifications",
  "Visibility",
];

function SectionLabel({ children }) {
  return <p className="kicker">{children}</p>;
}

export default function CaseBottleneck() {
  useTitle("The Manager Who Became the Bottleneck — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="case-manager-bottleneck-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative py-20 lg:py-28">
          <Reveal>
            <Link
              to="/ideas"
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-glow"
              data-testid="case-back-link"
            >
              <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
              How Work Actually Works
            </Link>
            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
              People · Operating Model
            </p>
            <h1 className="mt-6 max-w-4xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              The Manager Who Became the Bottleneck
            </h1>
            <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-slate-500">
              A case study in key-person dependency
            </p>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-300 sm:text-xl">
              When everything seems to depend on one manager, the obvious answer is usually:
              they need to delegate.
            </p>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-slate-400 sm:text-xl">
              But what if delegation isn't actually the problem?
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wrap border-b border-line/50 py-16 lg:py-24">
        <Reveal>
          <div className="mx-auto max-w-sm" data-testid="case-video-slot">
            <div className="relative aspect-[9/16] overflow-hidden border border-line/60 bg-charcoal/30">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${VIDEO_MANAGER_BOTTLENECK}`}
                title="The Manager Who Became the Bottleneck — Operating Reality"
                className="absolute inset-0 h-full w-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
              The short version —{" "}
              <a
                href={YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-glow/80 transition-colors hover:text-glow"
                data-testid="case-video-channel-link"
              >
                Operating Reality on YouTube
              </a>
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>The situation</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                A manager has become the person everyone goes to.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-8 divide-y divide-line/50 border-y border-line/50" data-testid="case-ask-manager">
                {ASK_THE_MANAGER.map((line) => (
                  <li key={line} className="flex items-baseline justify-between gap-6 py-4">
                    <span className="text-sm leading-relaxed text-slate-300 sm:text-base">{line}</span>
                    <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.22em] text-glow/70">
                      → ask the manager
                    </span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 space-y-4 text-base leading-relaxed text-slate-400">
                <p>
                  The manager is constantly interrupted, constantly apologising for delays, and
                  constantly promising to get back to people.
                </p>
                <p>
                  They are genuinely overloaded.{" "}
                  <span className="text-slate-200">But the pattern continues.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50 bg-charcoal/20">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>What everyone sees</SectionLabel>
              <p className="mt-8 text-balance text-2xl font-medium leading-snug tracking-tight text-slate-100 sm:text-3xl">
                “The manager is the bottleneck.”
              </p>
              <p className="mt-8 text-base leading-relaxed text-slate-400">
                The obvious recommendations are:
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-6 flex flex-wrap gap-2" data-testid="case-obvious-recommendations">
                {OBVIOUS_RECOMMENDATIONS.map((r) => (
                  <li
                    key={r}
                    className="border border-line/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400"
                  >
                    {r}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-base leading-relaxed text-slate-200">
                But these treat the person as the problem.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>Look underneath</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                The more useful questions are:
              </p>
            </Reveal>
            <ol className="mt-8 border-b border-line/50" data-testid="case-underneath-questions">
              {UNDERNEATH_QUESTIONS.map((q, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <li className="group flex items-baseline gap-6 border-t border-line/50 py-5">
                    <span className="font-mono text-xs tracking-[0.3em] text-glow/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-balance text-lg font-medium leading-snug tracking-tight text-slate-200 transition-colors duration-300 group-hover:text-white sm:text-xl">
                      {q}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ol>
            <Reveal delay={0.2}>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-glow/80">
                This is where the problem changes.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50 bg-charcoal/20">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>The real dependency</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                The organisation may have gradually created a model where:
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div
                className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-4 border border-line/60 bg-ink p-6 lg:p-8"
                data-testid="case-equation"
              >
                <span className="border border-glow/70 bg-glow/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-glow">
                  Manager
                </span>
                <span className="font-mono text-lg text-slate-500">=</span>
                {EQUATION_PARTS.map((p, i) => (
                  <span key={p} className="flex items-center gap-4">
                    <span className="border border-line/70 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-slate-300">
                      {p}
                    </span>
                    {i < EQUATION_PARTS.length - 1 && (
                      <span className="font-mono text-lg text-slate-600">+</span>
                    )}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 space-y-4 text-base leading-relaxed text-slate-400">
                <p>
                  The manager didn't necessarily choose to become a bottleneck.{" "}
                  <span className="text-slate-200">The operating model made them one.</span>
                </p>
                <p className="text-sm leading-relaxed text-slate-500">
                  Key-person dependency is a recognised finding pattern in the Operating Reality
                  diagnostic methodology: critical knowledge or activity depends on one person or
                  a small number of people.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>Root cause</SectionLabel>
              <h2 className="mt-6 text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-50 sm:text-4xl">
                Key-person dependency.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-8 space-y-6">
                <blockquote className="border-l-2 border-line pl-6 text-base italic leading-relaxed text-slate-500">
                  Not simply: “The manager doesn't delegate.”
                </blockquote>
                <p className="text-base leading-relaxed text-slate-300 sm:text-lg">
                  But potentially: critical organisational knowledge and decision rights have not
                  been distributed or embedded into the operating model.
                </p>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-glow/80">
                  That distinction matters.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50 bg-charcoal/20">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>Diagnose it yourself</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-slate-400">Ask:</p>
            </Reveal>
            <ol className="mt-8 border-b border-line/50" data-testid="case-six-questions">
              {SIX_QUESTIONS.map((q, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="group flex items-baseline gap-6 border-t border-line/50 py-5">
                    <span className="font-mono text-xs tracking-[0.3em] text-glow/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="text-balance text-lg font-medium leading-snug tracking-tight text-slate-200 transition-colors duration-300 group-hover:text-white sm:text-xl">
                      {q}
                    </p>
                  </li>
                </Reveal>
              ))}
            </ol>
            <Reveal delay={0.2}>
              <p className="mt-6 text-sm italic leading-relaxed text-slate-500">
                That last question is particularly powerful.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>What to change</SectionLabel>
              <div className="mt-6 space-y-3 text-base leading-relaxed">
                <p className="text-slate-500 line-through decoration-slate-700">
                  Don't start with: “How do we make the manager faster?”
                </p>
                <p className="text-slate-200">Start with:</p>
              </div>
            </Reveal>
          </div>
          <ol className="mt-10 border-b border-line/50" data-testid="case-change-steps">
            {CHANGE_STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.06}>
                <li className="grid gap-4 border-t border-line/50 py-8 md:grid-cols-12">
                  <span className="font-mono text-xs tracking-[0.3em] text-glow/70 md:col-span-1">
                    {String(s.n).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-11">
                    <h3 className="text-xl font-medium tracking-tight text-slate-100">{s.title}</h3>
                    {s.sub ? (
                      <ul className="mt-4 space-y-2">
                        {s.sub.map((sub) => (
                          <li key={sub} className="flex items-baseline gap-3 text-sm leading-relaxed text-slate-400">
                            <span className="inline-block h-1.5 w-1.5 shrink-0 rotate-45 border border-glow/60" aria-hidden="true" />
                            {sub}?
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">{s.text}</p>
                    )}
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-b border-line/50 bg-charcoal/20">
        <div className="wrap py-16 lg:py-24">
          <div className="max-w-3xl">
            <Reveal>
              <SectionLabel>What technology can — and cannot — do</SectionLabel>
              <p className="mt-6 text-base leading-relaxed text-slate-400">
                Technology may eventually help with:
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <ul className="mt-8 flex flex-wrap gap-2" data-testid="case-tech-list">
                {TECH_LIST.map((t) => (
                  <li
                    key={t}
                    className="border border-line/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 transition-colors duration-300 hover:border-glow/50 hover:text-glow"
                  >
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-10 space-y-4 text-base leading-relaxed text-slate-400">
                <p className="text-slate-200">But technology is not the first intervention.</p>
                <p>
                  Because if the organisation hasn't decided who should make the decision, then
                  automating the workflow doesn't solve the problem.
                </p>
                <p className="text-lg font-medium text-slate-100">
                  It just moves the queue somewhere else.
                </p>
                <p className="text-sm leading-relaxed text-slate-500">
                  The Operating Reality methodology follows this principle: understand the
                  operating problem and required capability before selecting technology — and
                  distinguish symptoms from root causes.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-20 lg:py-28">
          <Reveal className="max-w-4xl" data-testid="case-takeaway">
            <SectionLabel>The Operating Reality takeaway</SectionLabel>
            <p className="mt-8 text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Don't ask why one person is a bottleneck.{" "}
              <span className="text-glow">Ask why the organisation needs that person so often.</span>
            </p>
            <div className="mt-10 max-w-2xl space-y-4 text-base leading-relaxed text-slate-400">
              <p>Sometimes the answer really is capacity.</p>
              <p>But sometimes: the organisation has turned one person's knowledge into a dependency.</p>
              <p className="text-slate-200">And that is an operating model problem.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-line/50">
        <div className="wrap py-16 lg:py-20">
          <Reveal className="max-w-3xl">
            <ChecklistGate />
          </Reveal>
        </div>
      </section>

      <DiagnosticTieIn dim="people" />

      <section className="bg-charcoal/20">
        <div className="wrap py-20 lg:py-28">
          <Reveal className="max-w-3xl">
            <SectionLabel>Try this</SectionLabel>
            <h2 className="mt-6 text-balance text-3xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl">
              Could your organisation survive two weeks without its key people?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Try mapping one critical process and mark every step that depends on a specific
              individual.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              You may find your biggest operational risk isn't a system.{" "}
              <span className="text-slate-100">It's a person.</span>
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/consulting" className="btn-primary" data-testid="case-cta-consulting">
                Explore Consulting <ArrowRight size={14} />
              </Link>
              <Link to="/contact" className="btn-ghost" data-testid="case-cta-conversation">
                Start a conversation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <RelatedPatterns slug="manager-bottleneck" />
    </main>
  );
}
